import type { AIEquipmentIdentification, AIRawExtraction } from '@/types'
import {
  buildDisambiguationPrompt,
  buildCapacityGuidance,
  buildDualFuelGuidance,
  buildMultiColumnGuidance,
} from './extraction/label-guidance'

import { aiProxy } from './proxy'

interface GPTVisionResponse {
  choices: Array<{
    message: {
      content: string
    }
  }>
}

const EQUIPMENT_IDENTIFICATION_PROMPT = `You are a precise OCR system that reads equipment labels.

## YOUR ONLY JOB: READ EXACTLY WHAT IS PRINTED

Read ALL text visible on this equipment label/nameplate EXACTLY as printed.
Do NOT interpret, infer, calculate, or assume ANY values.
Do NOT use knowledge from model numbers, part numbers, or patterns.

## STRICT RULES

1. **ONLY report values that have an explicit label next to them**
   - If you see "CAPACITY RATED/MEASURED 65/64 gal" → capacity is 65 (the number after the label)
   - If you see "MODEL NO. SWR125N" → model is SWR125N (do NOT extract 125 as capacity)
   - The "125" in "SWR125N" is part of the model number, NOT a capacity value

2. **NEVER infer or calculate values**
   - Do NOT assume model number SWR125N means 125 gallons
   - Do NOT assume model number contains BTU ratings
   - Do NOT fill in values based on equipment knowledge

3. **Read label-value pairs literally**
   - "INPUT RATING 125,000 BTU/HR" → fuel.input_btu_hr: 125000
   - "CAPACITY RATED/MEASURED 65/64 gal" → capacity.gallons: 65
   - "RECOVERY @ 100F RISE 145 GPH" → water.recovery_rate_gph: 145 (NOT capacity.gallons!)
   - "SERIES 100" → series: "100" (NOT capacity)

5. **Model and serial numbers — capture the COMPLETE value**
   - Read the ENTIRE string on the MODEL/SERIAL line, including all spaces, dashes, and segments
   - "MODEL: 150 CPS S 150 CPS CL1" → model_number: "150 CPS S 150 CPS CL1" (ALL segments)
   - "SERIAL: 050SK15913-00/0000706" → serial_number: "050SK15913-00/0000706" (full string including slashes)
   - Do NOT truncate, split, or drop trailing segments — the full value is one field
   - Equipment model numbers often contain spaces, letters, and numbers mixed together

4. **If no explicit label exists for a field, use null**
   - No "CAPACITY" or "GAL" label visible? → gallons: null
   - No "VOLTAGE" or "V" label visible? → voltage: null

${buildMultiColumnGuidance()}

${buildDualFuelGuidance()}

${buildCapacityGuidance()}

${buildDisambiguationPrompt()}

## HOW TO READ THE LABEL

1. First, transcribe ALL visible text into raw_text field
2. Identify the column layout (single column, 2-column, or multi-column)
3. Then, ONLY fill in JSON fields where you see an explicit label
4. For each value you extract, it MUST have a printed label NEAREST to it (by proximity, not by left-to-right reading order)

Example: If label shows "MODEL NO. ABC123" and "CAPACITY 50 GAL"
- model_number: "ABC123" ✓ (has "MODEL NO." label)
- gallons: 50 ✓ (has "CAPACITY" and "GAL" labels)
- Do NOT extract "123" from model as capacity

Respond with valid JSON:
{
  "manufacturer": "string or null",
  "series": "string or null (product line/series name, e.g., SWR, Shield, XR15)",
  "model_number": "string or null",
  "serial_number": "string or null",
  "part_number": "string or null",
  "asset_tag": "string or null",
  "category": "string (RTU, Split System, Walk-in, Exhaust Fan, Dishwasher, etc.)",
  "subcategory": "string or null",
  "confidence": 0.0-1.0,
  "country_of_origin": "string or null",

  "electrical": {
    "voltage": number or null,
    "voltage_range": {"min": number, "max": number} or null,
    "phase": number or null,
    "fla": number or null,
    "rla": number or null,
    "lra": number or null,
    "amps": number or null,
    "amps_booster": number or null,
    "amps_total": number or null,
    "amps_l1": number or null,
    "amps_l2": number or null,
    "amps_l3": number or null,
    "mca": number or null,
    "mocp": number or null,
    "hz": number or null,
    "watts": number or null,
    "watts_upper": number or null,
    "watts_lower": number or null,
    "kw": number or null,
    "kw_total": number or null,
    "nema_rating": "string or null",
    "wire_count": number or null,
    "short_circuit_rating_ka": number or null
  },

  "capacity": {
    "btu": number or null,
    "btu_heating": number or null,
    "btu_input": number or null,
    "btu_output": number or null,
    "tons": number or null,
    "hp": number or null,
    "cfm": number or null,
    "gpm": number or null,
    "psi": number or null,
    "max_working_psi": number or null,
    "gallons": number or null,
    "cu_ft": number or null,
    "lbs_per_day": number or null,
    "lbs": number or null,
    "racks_per_hour": number or null,
    "qt": number or null,
    "seer": number or null,
    "eer": number or null,
    "rpm": number or null,
    "esp": number or null,
    "thermal_efficiency": number or null,
    "max_air_temp_f": number or null
  },

  "ice_machine": {
    "ice_type": "string or null (cube, flake, nugget, crescent)",
    "bin_capacity_lbs": number or null,
    "water_usage_gpd": number or null
  },

  "cooking": {
    "burner_count": number or null,
    "btu_per_burner": number or null,
    "cooking_surface_sqft": number or null,
    "oil_capacity_lbs": number or null,
    "rack_count": number or null,
    "pan_capacity": "string or null",
    "belt_width_in": number or null,
    "deck_count": number or null
  },

  "refrigeration_specs": {
    "r_value": number or null,
    "panel_thickness_in": number or null,
    "temp_range_min_f": number or null,
    "temp_range_max_f": number or null,
    "door_count": number or null
  },

  "ventilation": {
    "hood_type": "string or null (type_i, type_ii)",
    "linear_ft": number or null,
    "capture_velocity_fpm": number or null,
    "wheel_diameter_in": number or null,
    "nozzle_count": number or null
  },

  "warewashing": {
    "tank_count": number or null,
    "tank_temp_f": number or null,
    "rinse_temp_f": number or null,
    "booster_required": boolean or null
  },

  "beverage": {
    "group_heads": number or null,
    "valve_count": number or null,
    "barrel_count": number or null,
    "tap_count": number or null,
    "bottle_capacity": number or null
  },

  "building_system": {
    "kva_rating": number or null,
    "circuit_count": number or null,
    "zone_count": number or null
  },

  "water": {
    "recovery_rate_gph": number or null,
    "temp_rise_f": number or null,
    "gpm_rating": number or null
  },

  "water_heater": {
    "type": "string or null (tank, tankless, heat_pump, indirect, commercial_boiler)",
    "tank_capacity_gallons": number or null,
    "energy_factor": number or null,
    "uef_rating": number or null,
    "venting_type": "string or null (direct_vent, power_vent, atmospheric, condensing)",
    "recovery_rate_gph": number or null,
    "first_hour_rating": number or null,
    "gas_line_size": "string or null"
  },

  "refrigerant": {
    "type": "string or null",
    "charge_oz": number or null,
    "charge_lbs": number or null,
    "comp_amps": number or null,
    "cond_fan_amps": number or null,
    "evap_fan_amps": number or null,
    "def_heater_watts": number or null
  },

  "fuel": {
    "type": "string or null (natural_gas, propane, oil)",
    "input_btu_hr": number or null,
    "minimum_input_btu_hr": number or null,
    "orifice_size": "string or null",
    "gas_pressure": "string or null"
  },

  "physical": {
    "width_in": number or null,
    "height_in": number or null,
    "depth_in": number or null,
    "weight_lbs": number or null,
    "surface_area_sqft": number or null,
    "material_type": "string or null"
  },

  "energy": {
    "consumption_whday": number or null,
    "consumption_kwh_year": number or null,
    "energy_star": boolean or null
  },

  "location_context": {
    "tag": "string or null",
    "zone": "string or null",
    "asset_tag": "string or null",
    "circuit": "string or null"
  },

  "dates": {
    "manufacture_date": "YYYY-MM-DD or YYYY-MM or YYYY or null",
    "date_code": "string or null",
    "service_due": "string or null"
  },

  "certifications": ["array of visible certifications"],
  "identified_features": ["other notable features"],
  "condition_notes": ["visible wear, damage, etc."],
  "warnings": ["important safety/installation warnings"],
  "raw_text": "any other text on label not captured above"
}

IMPORTANT: Only report what you can clearly read. Use null for anything not visible. Never guess at numbers.`

// Full extraction with all label data - use this for testing/development
export async function extractEquipmentData(
  imageFile: File
): Promise<AIRawExtraction> {
  const base64Image = await fileToBase64(imageFile)

  const data = await aiProxy<GPTVisionResponse>('openai', '/v1/chat/completions', {
    model: 'gpt-4o',
    messages: [
      {
        role: 'user',
        content: [
          {
            type: 'text',
            text: EQUIPMENT_IDENTIFICATION_PROMPT,
          },
          {
            type: 'image_url',
            image_url: {
              url: `data:${imageFile.type};base64,${base64Image}`,
              detail: 'high',
            },
          },
        ],
      },
    ],
    max_tokens: 8192,
    temperature: 0,
  })
  const content = data.choices[0]?.message?.content

  if (!content) {
    throw new Error('No response from GPT-4 Vision')
  }

  try {
    // Strip markdown code blocks if present (```json ... ```)
    let cleanContent = content
    if (content.includes('```')) {
      cleanContent = content.replace(/```json\n?/g, '').replace(/```\n?/g, '')
    }

    const jsonMatch = cleanContent.match(/\{[\s\S]*\}/)
    let jsonStr = jsonMatch?.[0]

    // If no complete JSON object found, the response was likely truncated
    if (!jsonStr) {
      const partialMatch = cleanContent.match(/\{[\s\S]*/)
      if (partialMatch) {
        jsonStr = repairTruncatedJson(partialMatch[0])
        console.warn('⚠️ GPT response was truncated — attempting JSON repair')
      } else {
        throw new Error('No JSON found in response')
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let parsed: any
    try {
      parsed = JSON.parse(jsonStr)
    } catch {
      // JSON.parse failed even with matched braces — try repair on the full match
      const repaired = repairTruncatedJson(jsonStr)
      console.warn('⚠️ GPT JSON malformed — attempting repair')
      parsed = JSON.parse(repaired)
    }

    // Log full response for development analysis
    console.log('🔍 AI Raw Extraction:', JSON.stringify(parsed, null, 2))

    // Return with defaults for missing nested objects
    return {
      manufacturer: parsed.manufacturer ?? null,
      model_number: parsed.model_number ?? null,
      serial_number: parsed.serial_number ?? null,
      part_number: parsed.part_number ?? null,
      asset_tag: parsed.asset_tag ?? null,
      category: parsed.category || 'Unknown',
      subcategory: parsed.subcategory ?? null,
      confidence: parsed.confidence ?? 0.5,
      country_of_origin: parsed.country_of_origin ?? null,
      electrical: {
        voltage: parsed.electrical?.voltage ?? null,
        voltage_range: parsed.electrical?.voltage_range ?? null,
        phase: parsed.electrical?.phase ?? null,
        fla: parsed.electrical?.fla ?? null,
        rla: parsed.electrical?.rla ?? null,
        lra: parsed.electrical?.lra ?? null,
        hz: parsed.electrical?.hz ?? null,
        watts: parsed.electrical?.watts ?? null,
        watts_upper: parsed.electrical?.watts_upper ?? null,
        watts_lower: parsed.electrical?.watts_lower ?? null,
        kw: parsed.electrical?.kw ?? null,
        kw_total: parsed.electrical?.kw_total ?? null,
        amps: parsed.electrical?.amps ?? null,
        amps_booster: parsed.electrical?.amps_booster ?? null,
        amps_total: parsed.electrical?.amps_total ?? null,
        amps_l1: parsed.electrical?.amps_l1 ?? null,
        amps_l2: parsed.electrical?.amps_l2 ?? null,
        amps_l3: parsed.electrical?.amps_l3 ?? null,
        mca: parsed.electrical?.mca ?? null,
        mocp: parsed.electrical?.mocp ?? null,
        nema_rating: parsed.electrical?.nema_rating ?? null,
        wire_count: parsed.electrical?.wire_count ?? null,
        short_circuit_rating_ka: parsed.electrical?.short_circuit_rating_ka ?? null,
      },
      capacity: {
        btu: parsed.capacity?.btu ?? null,
        btu_heating: parsed.capacity?.btu_heating ?? null,
        btu_input: parsed.capacity?.btu_input ?? null,
        btu_output: parsed.capacity?.btu_output ?? null,
        tons: parsed.capacity?.tons ?? null,
        hp: parsed.capacity?.hp ?? null,
        cfm: parsed.capacity?.cfm ?? null,
        gpm: parsed.capacity?.gpm ?? null,
        psi: parsed.capacity?.psi ?? null,
        max_working_psi: parsed.capacity?.max_working_psi ?? null,
        gallons: parsed.capacity?.gallons ?? null,
        cu_ft: parsed.capacity?.cu_ft ?? null,
        lbs_per_day: parsed.capacity?.lbs_per_day ?? null,
        lbs: parsed.capacity?.lbs ?? null,
        racks_per_hour: parsed.capacity?.racks_per_hour ?? null,
        qt: parsed.capacity?.qt ?? null,
        seer: parsed.capacity?.seer ?? null,
        eer: parsed.capacity?.eer ?? null,
        rpm: parsed.capacity?.rpm ?? null,
        esp: parsed.capacity?.esp ?? null,
        thermal_efficiency: parsed.capacity?.thermal_efficiency ?? null,
        max_air_temp_f: parsed.capacity?.max_air_temp_f ?? null,
      },
      refrigerant: {
        type: parsed.refrigerant?.type ?? null,
        charge_oz: parsed.refrigerant?.charge_oz ?? null,
        charge_lbs: parsed.refrigerant?.charge_lbs ?? null,
        comp_amps: parsed.refrigerant?.comp_amps ?? null,
        cond_fan_amps: parsed.refrigerant?.cond_fan_amps ?? null,
        evap_fan_amps: parsed.refrigerant?.evap_fan_amps ?? null,
        def_heater_watts: parsed.refrigerant?.def_heater_watts ?? null,
      },
      fuel: parsed.fuel
        ? {
            type: parsed.fuel.type ?? null,
            input_btu_hr: parsed.fuel.input_btu_hr ?? null,
            minimum_input_btu_hr: parsed.fuel.minimum_input_btu_hr ?? null,
            orifice_size: parsed.fuel.orifice_size ?? null,
            gas_pressure: parsed.fuel.gas_pressure ?? null,
          }
        : undefined,
      physical: {
        width_in: parsed.physical?.width_in ?? null,
        height_in: parsed.physical?.height_in ?? null,
        depth_in: parsed.physical?.depth_in ?? null,
        weight_lbs: parsed.physical?.weight_lbs ?? null,
        surface_area_sqft: parsed.physical?.surface_area_sqft ?? null,
        material_type: parsed.physical?.material_type ?? null,
      },
      dates: {
        manufacture_date: parsed.dates?.manufacture_date ?? null,
        date_code: parsed.dates?.date_code ?? null,
        service_due: parsed.dates?.service_due ?? null,
      },
      energy: parsed.energy
        ? {
            consumption_whday: parsed.energy.consumption_whday ?? null,
            consumption_kwh_year: parsed.energy.consumption_kwh_year ?? null,
            energy_star: parsed.energy.energy_star ?? null,
          }
        : undefined,
      location_context: parsed.location_context
        ? {
            tag: parsed.location_context.tag ?? null,
            zone: parsed.location_context.zone ?? null,
            building: parsed.location_context.building ?? null,
            asset_tag: parsed.location_context.asset_tag ?? null,
            circuit: parsed.location_context.circuit ?? null,
          }
        : undefined,
      // Equipment-specific objects
      ice_machine: parsed.ice_machine
        ? {
            ice_type: parsed.ice_machine.ice_type ?? null,
            bin_capacity_lbs: parsed.ice_machine.bin_capacity_lbs ?? null,
            water_usage_gpd: parsed.ice_machine.water_usage_gpd ?? null,
          }
        : undefined,
      cooking: parsed.cooking
        ? {
            burner_count: parsed.cooking.burner_count ?? null,
            btu_per_burner: parsed.cooking.btu_per_burner ?? null,
            cooking_surface_sqft: parsed.cooking.cooking_surface_sqft ?? null,
            oil_capacity_lbs: parsed.cooking.oil_capacity_lbs ?? null,
            rack_count: parsed.cooking.rack_count ?? null,
            pan_capacity: parsed.cooking.pan_capacity ?? null,
            belt_width_in: parsed.cooking.belt_width_in ?? null,
            deck_count: parsed.cooking.deck_count ?? null,
          }
        : undefined,
      refrigeration_specs: parsed.refrigeration_specs
        ? {
            r_value: parsed.refrigeration_specs.r_value ?? null,
            panel_thickness_in: parsed.refrigeration_specs.panel_thickness_in ?? null,
            temp_range_min_f: parsed.refrigeration_specs.temp_range_min_f ?? null,
            temp_range_max_f: parsed.refrigeration_specs.temp_range_max_f ?? null,
            door_count: parsed.refrigeration_specs.door_count ?? null,
          }
        : undefined,
      ventilation: parsed.ventilation
        ? {
            hood_type: parsed.ventilation.hood_type ?? null,
            linear_ft: parsed.ventilation.linear_ft ?? null,
            capture_velocity_fpm: parsed.ventilation.capture_velocity_fpm ?? null,
            wheel_diameter_in: parsed.ventilation.wheel_diameter_in ?? null,
            nozzle_count: parsed.ventilation.nozzle_count ?? null,
          }
        : undefined,
      warewashing: parsed.warewashing
        ? {
            tank_count: parsed.warewashing.tank_count ?? null,
            tank_temp_f: parsed.warewashing.tank_temp_f ?? null,
            rinse_temp_f: parsed.warewashing.rinse_temp_f ?? null,
            booster_required: parsed.warewashing.booster_required ?? null,
          }
        : undefined,
      beverage: parsed.beverage
        ? {
            group_heads: parsed.beverage.group_heads ?? null,
            valve_count: parsed.beverage.valve_count ?? null,
            barrel_count: parsed.beverage.barrel_count ?? null,
            tap_count: parsed.beverage.tap_count ?? null,
            bottle_capacity: parsed.beverage.bottle_capacity ?? null,
          }
        : undefined,
      building_system: parsed.building_system
        ? {
            kva_rating: parsed.building_system.kva_rating ?? null,
            circuit_count: parsed.building_system.circuit_count ?? null,
            zone_count: parsed.building_system.zone_count ?? null,
          }
        : undefined,
      water: parsed.water
        ? {
            recovery_rate_gph: parsed.water.recovery_rate_gph ?? null,
            temp_rise_f: parsed.water.temp_rise_f ?? null,
            gpm_rating: parsed.water.gpm_rating ?? null,
          }
        : undefined,
      certifications: parsed.certifications || [],
      identified_features: parsed.identified_features || [],
      condition_notes: parsed.condition_notes || [],
      warnings: parsed.warnings || [],
      raw_text: parsed.raw_text ?? null,
    }
  } catch (parseError) {
    console.error('Failed to parse GPT-4 Vision response:', content)
    throw new Error('Failed to parse equipment extraction response')
  }
}

// Legacy function - converts full extraction to simple identification format
export async function identifyEquipmentFromImage(
  imageFile: File
): Promise<AIEquipmentIdentification> {
  const fullExtraction = await extractEquipmentData(imageFile)

  return {
    manufacturer: fullExtraction.manufacturer || 'Unknown',
    model_number: fullExtraction.model_number || 'Not identified',
    category: fullExtraction.category || 'Unknown',
    confidence: fullExtraction.confidence,
    identified_features: fullExtraction.identified_features,
  }
}

/**
 * Attempt to repair truncated JSON from a cut-off API response.
 * Closes any open strings, arrays, and objects so JSON.parse can succeed.
 * Fields that were being written when truncation occurred will be lost,
 * but all complete fields before the cutoff will be preserved.
 */
function repairTruncatedJson(json: string): string {
  let repaired = json

  // If we're inside an unclosed string value, close it
  // Count unescaped quotes — if odd, we're mid-string
  const unescapedQuotes = repaired.match(/(?<!\\)"/g)
  if (unescapedQuotes && unescapedQuotes.length % 2 !== 0) {
    // Truncate back to the last complete key-value pair
    const lastCompleteComma = repaired.lastIndexOf(',')
    const lastCompleteBrace = repaired.lastIndexOf('}')
    const lastCompleteBracket = repaired.lastIndexOf(']')
    const cutPoint = Math.max(lastCompleteComma, lastCompleteBrace, lastCompleteBracket)
    if (cutPoint > 0) {
      repaired = repaired.substring(0, cutPoint + 1)
    } else {
      repaired += '"'
    }
  }

  // Remove any trailing comma before we close brackets
  repaired = repaired.replace(/,\s*$/, '')

  // Count open vs close brackets and braces
  let openBraces = 0
  let openBrackets = 0
  let inString = false

  for (let i = 0; i < repaired.length; i++) {
    const ch = repaired[i]
    if (ch === '\\' && inString) { i++; continue }
    if (ch === '"') { inString = !inString; continue }
    if (inString) continue
    if (ch === '{') openBraces++
    if (ch === '}') openBraces--
    if (ch === '[') openBrackets++
    if (ch === ']') openBrackets--
  }

  // Close any remaining open structures
  while (openBrackets > 0) { repaired += ']'; openBrackets-- }
  while (openBraces > 0) { repaired += '}'; openBraces-- }

  return repaired
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const result = reader.result as string
      // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64 = result.split(',')[1]
      resolve(base64)
    }
    reader.onerror = (error) => reject(error)
  })
}

// Additional helper for batch processing
export async function identifyMultipleEquipment(
  imageFiles: File[]
): Promise<AIEquipmentIdentification[]> {
  const results = await Promise.allSettled(
    imageFiles.map((file) => identifyEquipmentFromImage(file))
  )

  return results.map((result, index) => {
    if (result.status === 'fulfilled') {
      return result.value
    } else {
      console.error(`Failed to identify image ${index}:`, result.reason)
      return {
        manufacturer: 'Unknown',
        model_number: 'Identification failed',
        category: 'Unknown',
        confidence: 0,
        identified_features: [],
      }
    }
  })
}
